<template>
  <div class="profile-menu">
    <div
      class="profile-menu__controls"
      tabindex="0"
    >
      <div class="profile-menu__avatar-wrapper">
        <div
          v-if="userStore.isCopywriter && isShowEditToggler"
          :class="[
            'profile-menu__edit-icon',
            { 'profile-menu__edit-icon--active': userStore.isEditEnabled },
          ]"
          @click="userStore.toggleEditEnable"
        >
          <EditIcon />
        </div>

        <img
          class="profile-menu__avatar"
          :alt="userStore.user?.name"
          data-ignore-click-outside
          :src="userStore.user?.avatar"
          @click="isMenuOpened = !isMenuOpened"
        />

        <div
          v-if="userStore.isCopywriter && isShowEditToggler"
          class="profile-menu__edit-toggler edit-toggler"
        >
          <div class="edit-toggler__title">Редактирование</div>
          <a
            :class="[
              'edit-toggler__link',
              { 'edit-toggler__link--active': userStore.isEditEnabled },
            ]"
            @click="userStore.toggleEditEnable"
          />
        </div>
      </div>

      <div
        v-if="isMenuOpened"
        v-click-outside="onClickOutsideDropdown"
        class="profile-menu__dropdown-list dropdown-list"
      >
        <div
          v-if="userStore.isAdmin && isShowManageLink"
          class="dropdown-list__list"
        >
          <NuxtLink
            class="dropdown-list__item"
            to="/manage"
            external
          >
            Личный кабинет
          </NuxtLink>
        </div>

        <div class="dropdown-list__list">
          <a
            class="dropdown-list__item"
            @click="userStore.logout"
          >
            Выход
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { IGNORE_OUTSIDE_DATA_NAME } from '@/constants';

import EditIcon from './images/EditIcon.vue';

defineProps({
  isShowEditToggler: {
    type: Boolean,
    default: true,
  },
  isShowManageLink: {
    type: Boolean,
    default: true,
  },
});

const userStore = useUserStore();

const isMenuOpened = ref(false);

const onClickOutsideDropdown = (e: Event) => {
  const { dataset } = e.target as HTMLElement;

  if (!dataset || IGNORE_OUTSIDE_DATA_NAME in dataset) {
    return;
  }

  isMenuOpened.value = false;
};
</script>

<style lang="scss" scoped>
.profile-menu {
  align-self: flex-start;
  pointer-events: none;

  &__avatar-wrapper {
    display: flex;
    justify-content: flex-end;
    gap: 15px;
  }

  &__avatar,
  &__edit-icon {
    z-index: 100;
    width: 40px;
    height: 40px;
    cursor: pointer;
    pointer-events: auto;
  }

  &__edit-icon {
    background-color: #f10142;
    visibility: hidden;

    svg {
      padding: 5px 0 0 7px;
    }

    &:hover,
    &:has(~ .edit-toggler .edit-toggler__link:hover) {
      svg {
        stroke: #fff;
        stroke-width: 1.5;
      }
    }

    &:hover ~ .edit-toggler {
      .edit-toggler__link {
        font-weight: 600;
      }
    }
  }

  .edit-toggler {
    display: none;
    z-index: 99;
    position: absolute;
    top: 0;
    right: 0;
    width: 255px;
    height: 64px;
    pointer-events: auto;
    background-color: #fff;
    font-family: 'Opensans';
    font-weight: 400;
    font-size: 14px;
    letter-spacing: -0.1px;
    padding: 14px 19px 14px 18px;
    box-sizing: border-box;
    box-shadow: -3px 0px 8px 3px rgba(0, 0, 0, 0.1);

    &__title,
    &__link {
      color: #000000;
    }

    &__link {
      text-decoration: underline;

      &::after {
        content: 'Включить';
      }

      &:hover {
        font-weight: 600;
        opacity: 1;
      }

      &--active {
        &::after {
          content: 'Выключить';
        }
      }
    }
  }

  .dropdown-list {
    position: absolute;
    left: 0;
    top: 61px;
    width: 100%;
    padding: 26px 40px;
    z-index: 999;
    pointer-events: auto;
    background: white;
    box-shadow: -3px 0px 8px 3px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;

    &::before {
      content: '';
      display: block;
      position: absolute;
      width: 0;
      height: 0;
      right: 12px;
      top: -10px;
      border-style: solid;
      border-width: 10px 0 0 17px;
      border-color: transparent transparent transparent #fff;
    }

    &__list {
      &:not(:last-child) {
        border-bottom: 1px solid #dbdbdb;
        margin-bottom: 28px;
        padding-bottom: 30px;
      }
    }

    &__item {
      color: black;
      font-family: Montserrat, sans-serif;
      font-weight: 300;
      font-size: 15px;
      text-decoration: none;

      &:hover {
        opacity: 1;
      }

      &:not(:last-child) {
        margin-bottom: 20px;
      }
    }
  }
}

@media (min-width: 640px) {
  .profile-menu {
    .dropdown-list {
      padding: 40px;
      top: 62px;
      width: 345px;
      right: 0;
      left: auto;
    }

    &__avatar-wrapper {
      &:has(> .profile-menu__avatar:hover),
      &:has(> .edit-toggler:hover),
      &:has(> .profile-menu__edit-icon:hover) {
        .profile-menu__edit-icon {
          visibility: visible;
        }

        .edit-toggler {
          display: block;
        }
      }

      .profile-menu__edit-icon {
        &--active {
          visibility: visible;
          margin-left: 0;
        }
      }
    }
  }
}

@media (max-width: 639px) {
  .profile-menu__auth-btn {
    margin-top: 0px;
    font-size: 16px;
    width: 86px;
    height: 36px;
  }

  .profile-menu {
    margin-top: 0;

    &__edit-icon {
      display: none;
      visibility: hidden;
    }

    &__avatar {
      width: 29px;
      height: 29px;
    }

    .edit-toggler {
      display: none;
    }
  }
}
</style>
